import {
  AddressInput,
  AddressVerificationFragment,
  CheckoutDeliveryOption,
  CheckoutErrorFragment,
  CheckoutFragment,
  CheckoutUnprocessableErrorsFragment,
  Currency,
  DeliveryOption,
  DeliveryOptionsFragment,
  LineItemFulfillmentDetailsInput,
} from '@/gql';
import {
  isCheckoutErrorFragment,
  isCheckoutFragment,
  isCheckoutUnprocessableErrorFragment,
  isCheckoutUnprocessableErrorsFragment,
  isCurrency,
  isDeliveryMinervaErrorsFragment,
} from '@/lib/gql';
import {
  CheckoutErrorName,
  CheckoutPricingInformation,
  CheckoutUpsertParams,
  ErrorContext,
  GetShippingParams,
  Maybe,
  ShippingQuote,
  StripeAddressElementChangeEventValue,
  UpdateCheckoutAddressInput,
} from '@/types/checkout';
import { flattenMoney, isError, isNotNullish, normalizeMoney } from '@/lib/utils';
import { ShippingRate, Stripe } from '@stripe/stripe-js';
import {
  addressVerification,
  createAndVerifyCheckout,
  getCheckout,
  getDeliveryOptions,
  updateAndVerifyCheckout,
} from '@/api';
import cookieStorage from '@/lib/cookie-storage/browser';
import {
  deliveryOptionHasNameAndPrice,
  deliveryOptionsHaveQuotes,
  getDeliveryOptionsPayload,
} from '@/lib/express-checkout';
import {
  ADDRESS_VERIFICATION_SELECTION,
  CHECKOUT_DELIVERY_OPTION_TO_DELIVERY_OPTION,
  CHECKOUT_ERRORS,
  DELIVERY_OPTION_TO_CHECKOUT_DELIVERY_OPTION,
} from '@/constants/checkout';
import { buildSku } from '@/lib/product';
import CheckoutErrorObj, {
  generateErrorMessageFromError,
  generateUnknownErrorMessage,
} from '@/lib/checkout/CheckoutErrorObj';
import { CartItem } from '@/types/cart';
import { areEqualIgnoreCase, capitalizeFirstChar, convertSnakeToTitleCase } from '@/lib/string';
import { LineItemData } from '@/components/CheckoutSummaryLineItem';
import { DEFAULT_CURRENCY } from '@/constants';
import logger from '../logger';

/**
 * Converts a `DeliveryOption` to a `CheckoutDeliveryOption`.
 *
 * @param {DeliveryOption | undefined} deliveryOption - The delivery option to convert.
 * @returns {CheckoutDeliveryOption | undefined} - The corresponding checkout delivery option, or undefined if not found.
 */
const deliveryOptionToCheckoutDeliveryOption = (
  deliveryOption: DeliveryOption | undefined
): CheckoutDeliveryOption | undefined =>
  (deliveryOption && DELIVERY_OPTION_TO_CHECKOUT_DELIVERY_OPTION[deliveryOption]) || undefined;

/**
 * Converts a `CheckoutDeliveryOption` to a `DeliveryOption`.
 *
 * @param {CheckoutDeliveryOption | undefined} checkoutDeliveryOption - The checkout delivery option to convert.
 * @returns {DeliveryOption | undefined} - The corresponding delivery option, or undefined if not found.
 */
const checkoutDeliveryOptionToDeliveryOption = (
  checkoutDeliveryOption: CheckoutDeliveryOption | undefined
): DeliveryOption | undefined =>
  (checkoutDeliveryOption && CHECKOUT_DELIVERY_OPTION_TO_DELIVERY_OPTION[checkoutDeliveryOption]) || undefined;

/**
 * Maps a checkout error or unprocessable errors fragment to displayable error messages.
 *
 * @param {CheckoutErrorFragment | CheckoutUnprocessableErrorsFragment} error - The error fragment to process.
 * @param {Object} [ctx] - Optional context for error processing.
 * @param {string} [ctx.promoCode] - The promo code associated with the error, if any.
 * @returns {Object} - An object containing `checkoutError` and `promoCodeError` strings.
 */
const checkoutErrorToDisplay = (
  error: CheckoutErrorFragment | CheckoutUnprocessableErrorsFragment,
  ctx?: { promoCode?: string }
): {
  checkoutError: string | undefined;
  promoCodeError: string | undefined;
} => {
  let checkoutError: string | undefined;
  let promoCodeError: string | undefined;

  const latchCheckoutError = (value: string) => {
    if (!checkoutError && value) {
      checkoutError = value;
    }
  };
  const latchPromoError = (value: string) => {
    if (!promoCodeError && value) {
      promoCodeError = value;
    }
  };

  if (isCheckoutErrorFragment(error)) {
    if (error?.displayMessage) {
      checkoutError = error.displayMessage;
    }
  } else if (isCheckoutUnprocessableErrorsFragment(error)) {
    error.errors?.forEach((cursor) => {
      const ptr = cursor?.source?.pointer;
      switch (cursor?.code) {
        case 'value_not_supported':
          if (ptr === '/attributes/currency') {
            latchCheckoutError('Invalid currency option.');
          } else if (ptr === '/attributes/deliveryOption') {
            latchCheckoutError('Invalid delivery option.');
          }
          break;
        case 'value_is_required':
          if (ptr === '/attributes/deliveryOption') {
            latchCheckoutError('Delivery option is required.');
          }
          break;
        case 'invalid_value':
          if (ptr === '/attributes/promoCode') {
            if (ctx?.promoCode) {
              latchPromoError(`Promo code ('${ctx?.promoCode}') is invalid. Please try a different code.`);
            } else {
              latchPromoError('Promo code is invalid. Please try a different code.');
            }
          }
          break;
        case 'variants_must_belong_to_same_seller':
          if (ptr === '/attributes/lineItems') {
            latchCheckoutError('Items from multiple sellers.');
          }
          break;
        default:
          break;
      }
    });
  }

  if (!checkoutError && !promoCodeError) {
    logger.warn('Unexpected unprocessable checkout error:', JSON.stringify(error));
    checkoutError = 'Unexpected error, please try again.';
  }

  return { checkoutError, promoCodeError };
};

/**
 * Builds the checkout confirmation route URL - which is where they are redirected after PI intialization for confirmation
 *
 * @param {Pick<CheckoutFragment, 'orderLookupNumber'>} checkout - The checkout object containing the order lookup number.
 * @returns {string} - The confirmation route URL.
 */
const buildCheckoutConfirmationRoute = (checkout: Pick<CheckoutFragment, 'orderLookupNumber'>): string =>
  `/confirmation?orderLookupNumber=${checkout.orderLookupNumber || ''}`;

/**
 * Checks if the given object is a `DeliveryOptionsFragment`.
 *
 * @param {DeliveryOptionsFragment | unknown} deliveryOptions - The object to check.
 * @returns {boolean} - True if the object is a `DeliveryOptionsFragment`, false otherwise.
 */
const isDeliveryOptionsFragment = (
  deliveryOptions: DeliveryOptionsFragment | unknown
): deliveryOptions is DeliveryOptionsFragment =>
  Boolean(
    deliveryOptions &&
      typeof deliveryOptions === 'object' &&
      // eslint-disable-next-line no-underscore-dangle
      (deliveryOptions as DeliveryOptionsFragment).__typename === 'DeliveryOptions'
  );

/**
 * Checks if the given object is an instance of `CheckoutErrorObj`.
 *
 * @param {unknown} obj - The object to check.
 * @returns {boolean} - True if the object is a `CheckoutErrorObj`, false otherwise.
 */
const isCheckoutErrorObj = (obj: unknown): obj is CheckoutErrorObj => obj instanceof CheckoutErrorObj;

/**
 * Checks if the given string is a valid `CheckoutErrorName`.
 *
 * @param {string} key - The string to check.
 * @returns {boolean} - True if the string is a valid `CheckoutErrorName`, false otherwise.
 */
const isCheckoutErrorName = (key: string): key is CheckoutErrorName => {
  return Object.keys(CHECKOUT_ERRORS).includes(key);
};

/**
 * Retrieves the appropriate unknown error name for a given error context
 *
 * @param {ErrorContext} ctx - The error context (confirm, customer etc.).
 * @returns {CheckoutErrorName} - The corresponding unknown error name.
 */
const getUnknownErrorByContext = (ctx: ErrorContext): CheckoutErrorName => {
  const key = `UNKNOWN_ERROR__${ctx.toUpperCase()}`;
  if (isCheckoutErrorName(key)) {
    return key;
  }
  return 'GENERIC_ERROR';
};

/**
 * Builds line item fulfillment details from the cart (validatedCartProducts).
 *
 * @param {CartItem[]} cart - The cart items.
 * @returns {LineItemFulfillmentDetailsInput[]} - The line item fulfillment details.
 */
const buildLineItemsFulfillmentDetailsFromCart = (cart: CartItem[]): LineItemFulfillmentDetailsInput[] =>
  cart.map((item) => {
    const sku = buildSku(item.itemGroupId, item.variationId, item.sizeId);
    return {
      sku,
      quantity: item.quantity,
      fulfillmentDetails: {
        productionTechniques: [],
      },
    };
  });

/**
 * Ensures the given error is a `CheckoutErrorObj` - attempts to convert some known errors, or wrap in a generic CheckoutErrorObj
 *
 * @param {unknown} error - The error to check.
 * @param {ErrorContext} ctx - The error context.
 * @param {boolean} [displayErrorMessage=false] - Whether to display the error message.
 * @returns {CheckoutErrorObj} - The ensured `CheckoutErrorObj`.
 */
const ensureIsCheckoutErrorObj = (error: unknown, ctx: ErrorContext, displayErrorMessage = false): CheckoutErrorObj => {
  if (isCheckoutErrorObj(error)) {
    return error;
  }

  if (isCheckoutErrorFragment(error)) {
    const message = error.message || generateUnknownErrorMessage(error);
    return new CheckoutErrorObj('INVALID_CHECKOUT', message, error.displayMessage || undefined);
  }

  if (isCheckoutUnprocessableErrorFragment(error)) {
    const message = error.code || generateUnknownErrorMessage(error);
    return new CheckoutErrorObj('INVALID_CHECKOUT', message || 'unknown error');
  }

  if (isCheckoutUnprocessableErrorsFragment(error)) {
    let errorMessage = 'Unknown error occurred';
    if (error.errors?.length > 0) {
      errorMessage = error.errors.map((e) => `${e.code}: ${e.source.pointer}`).join('\n');
    }
    return new CheckoutErrorObj('INVALID_CHECKOUT', errorMessage);
  }

  if (isDeliveryMinervaErrorsFragment(error)) {
    let errorMessage = 'Unknown Minerva error occurred';
    if (error.errors && error.errors?.length > 0) {
      errorMessage = error.errors
        .map((e) => `${e.code || 'Unknown code'}: ${e.detail || 'No details provided'}`)
        .join('\n');
    }
    return new CheckoutErrorObj('MINERVA_ERROR', errorMessage);
  }

  if (isError(error)) {
    const message = error.message || generateUnknownErrorMessage(error);
    return new CheckoutErrorObj(
      getUnknownErrorByContext(ctx),
      message,
      displayErrorMessage ? message : undefined,
      ctx,
      error
    );
  }

  return new CheckoutErrorObj(
    'GENERIC_ERROR',
    `An unexpected error occurred, attempting to stringify: ${JSON.stringify(error)}`,
    undefined,
    undefined
  );
};

/**
 * Converts a suggested address fragment (from smarty streets) to a partial checkout address for update checkout use.
 *
 * @param {AddressVerificationFragment} address - The suggested address fragment.
 * @returns {Partial<StripeAddressElementChangeEventValue['address']>} - The partial checkout address.
 */
const suggestedAddressFragmentToPartialCheckoutAddress = (
  address: AddressVerificationFragment
): Partial<StripeAddressElementChangeEventValue['address']> => {
  const correction = address.corrections?.[0];
  if (!correction || !correction.components) {
    return {};
  }

  const {
    delivery_line_1: line1,
    components: {
      city_name: city,
      state_abbreviation: state,
      // eslint-disable-next-line camelcase
      zipcode: postal_code,
      // eslint-disable-next-line camelcase
      plus4_code,
    } = {},
  } = correction;

  return {
    line1: line1 || '',
    city: city || '',
    state: state || '',
    line2: '', // We always reset this because smarty streets doesn't do a line2 - they append it to line 1
    // eslint-disable-next-line camelcase
    postal_code: postal_code ? `${postal_code}${plus4_code ? `-${plus4_code}` : ''}` : undefined,
  };
};

/**
 * Handles the selected of suggested or original address from smarty streets validation
 *
 * @param {string} selection - The selected address type.
 * @param {AddressVerificationFragment} suggestedAddress - The suggested address fragment.
 * @param {StripeAddressElementChangeEventValue} customerInfo - The customer information.
 * @returns {StripeAddressElementChangeEventValue} - The updated customer information.
 */
const handleVerificationSelectedAddress = (
  selection: string,
  suggestedAddress: AddressVerificationFragment,
  customerInfo: StripeAddressElementChangeEventValue
): StripeAddressElementChangeEventValue => {
  if (selection === ADDRESS_VERIFICATION_SELECTION.SUGGESTED) {
    const updatedAddressValue = suggestedAddressFragmentToPartialCheckoutAddress(suggestedAddress);
    return { ...customerInfo, address: { ...customerInfo.address, ...updatedAddressValue } };
  } else {
    return customerInfo;
  }
};

/**
 * Parses a Stripe event name into first and last name.
 *
 * @param {string} name - The event name.
 * @returns {Partial<StripeAddressElementChangeEventValue>} - The parsed first and last name.
 */
const parseStripeEventNameToFirstLast = (name: string): Partial<StripeAddressElementChangeEventValue> => {
  const [firstName, ...rest] = name.split(' ');
  return { firstName, lastName: rest.join(' ') };
};

/**
 * Maps delivery options from Minerva to Shipping Rates for che3ckout state
 *
 * @param {DeliveryOptionsFragment} deliveryOptions - The delivery options fragment.
 * @returns {ShippingRate[]} - The mapped shipping rates.
 */
const mapDeliveryOptionsToShippingRates = (deliveryOptions: DeliveryOptionsFragment): ShippingRate[] =>
  deliveryOptionsHaveQuotes(deliveryOptions)
    ? (deliveryOptions.attributes.quotes
        .map((quote) =>
          deliveryOptionHasNameAndPrice(quote)
            ? {
                id: DELIVERY_OPTION_TO_CHECKOUT_DELIVERY_OPTION[quote?.name || 'standard'],
                displayName: convertSnakeToTitleCase(quote.name as string),
                amount: quote.cost.amount,
              }
            : null
        )
        .filter(isNotNullish) as ShippingRate[])
    : [];

/**
 * Retrieves shipping rates based on the provided parameters.
 *
 * @param {CheckoutFragment} checkout - The checkout fragment.
 * @param {Currency} currency - The currency.
 * @param {AddressInput} address - The address input.
 * @param {string} storeSlug - The store slug.
 * @param {boolean} [mapForStripe=true] - Whether to map the rates for Stripe.
 * @returns {Promise<ShippingRate[] | DeliveryOptionsFragment>} - The shipping rates or delivery options fragment.
 */
const getShippingRates = async (
  checkout: CheckoutFragment,
  currency: Currency,
  address: AddressInput,
  storeSlug: string,
  mapForStripe: boolean = true
): Promise<ShippingRate[] | DeliveryOptionsFragment> => {
  if (!address.countryCode) {
    throw new Error(`Unable to calculate shipping rates given input: ${address}`);
  }
  const deliveryOptionsPayload = getDeliveryOptionsPayload(
    checkout,
    currency,
    address.countryCode === 'US' ? 'USA' : address.countryCode,
    address.stateCode || '',
    storeSlug
  );

  const deliveryOptions = await getDeliveryOptions(deliveryOptionsPayload);

  if (isDeliveryOptionsFragment(deliveryOptions)) {
    return mapForStripe ? mapDeliveryOptionsToShippingRates(deliveryOptions) : deliveryOptions;
  } else if (isDeliveryMinervaErrorsFragment(deliveryOptions)) {
    throw new Error(
      `Minerva Error occurred: ${JSON.stringify(deliveryOptions.errors?.map((e) => e.detail)) || 'Errors are empty'}`
    );
  } else {
    throw new Error(`Unknown error occurred getting shipping rates: ${JSON.stringify(deliveryOptions)}`);
  }
};

/**
 * Determines if user confirmation is required for the address based on Smarty Streets.
 *
 * @param {AddressVerificationFragment} verifiedAddress - The verified address fragment.
 * @param {UpdateCheckoutAddressInput} [customerAddress] - The customer address input.
 * @returns {boolean} - True if confirmation is required, false otherwise.
 */
const requiresUserConfirmation = (
  verifiedAddress: AddressVerificationFragment,
  customerAddress?: UpdateCheckoutAddressInput
) => {
  const { corrections } = verifiedAddress;
  if (!customerAddress) return true;
  if (!corrections || corrections.length === 0) return false;

  const { delivery_line_1: correctedLine1, last_line: lastLine, components } = corrections[0];
  if (!components) return false;

  const { city_name: correctedCity, state_abbreviation: correctedState, zipcode: correctedZip } = components;
  const {
    data: {
      customer: { address },
    },
  } = customerAddress;
  if (!address) return true;

  return Boolean(
    !areEqualIgnoreCase(address?.city, correctedCity) ||
      !areEqualIgnoreCase(address?.state, correctedState) ||
      !areEqualIgnoreCase(address?.zip, correctedZip) ||
      !areEqualIgnoreCase(address?.address1, correctedLine1) ||
      !(`${address?.city} ${address?.state} ${address?.zip}`.toLowerCase() === lastLine || '')
  );
};

/**
 * Creates a confirmation token using Stripe.
 *
 * @param {Stripe} stripe - The Stripe instance.
 * @param {Object} confirmTokenParams - The confirmation token parameters.
 * @returns {Promise<string>} - The confirmation token ID.
 */
const createConfirmationToken = async (stripe: Stripe, confirmTokenParams: any) => {
  try {
    const result = await stripe.createConfirmationToken(confirmTokenParams);

    if (result.error) {
      throw result.error;
    }

    return result.confirmationToken.id;
  } catch (e) {
    logger.error(`Error creating confirmation token:`, e);
    throw new CheckoutErrorObj('CONFIRMATION_TOKEN_GENERATION_FAILURE', generateErrorMessageFromError(e));
  }
};

/**
 * Confirms a payment using Stripe.
 *
 * @param {Stripe} stripe - The Stripe instance.
 * @param {string} clientSecret - The client secret.
 * @param {string} paymentIntentId - The payment intent ID.
 * @param {string} confirmationTokenId - The confirmation token ID.
 * @returns {Promise<void>} - Resolves when the payment is confirmed.
 */
const confirmPayment = async (
  stripe: Stripe,
  clientSecret: string,
  paymentIntentId: string,
  confirmationTokenId: string
) => {
  try {
    const { error } = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        confirmation_token: confirmationTokenId,
        return_url: `${window.location.origin}/new_checkout/confirm?id=${paymentIntentId}&secret=${clientSecret}`,
      },
    });
    if (error) {
      // Stripe errors have a type, we're using this to identify what error to throw
      if (error.type && error.type === 'card_error' && error.message) {
        throw new CheckoutErrorObj('STRIPE_CARD_ERROR', error.message, error.message);
      }
      if (error.code && error.code === 'payment_intent_customer_decline') {
        // No-op, customer cancelled payment
        return;
      }
      throw error;
    }
  } catch (e) {
    logger.error(`Error confirming payment:`, e);
    throw e;
  }
};

/**
 * Builds line items for the pricing hook from validated cart products.
 *
 * @param {any[]} validatedCartProducts - The validated cart products.
 * @returns {LineItemData[]} - The line items for the pricing hook.
 */
export const getLineItemsFromCart = (cartState: CartItem[]) => {
  return buildLineItemsFulfillmentDetailsFromCart(cartState);
};

/**
 * Upserts a checkout record for use on init during checkout process
 *
 * @param {LineItemData[]} summaryLineItems - The summary line items.
 * @param {any} cartMeta - The cart metadata.
 * @param {string} storeSlug - The store slug.
 * @returns {CheckoutPricingInformation | undefined} - The initial pricing information.
 */
export const upsertCheckout = async ({
  checkoutId,
  lineItems,
  storeSlug,
  currency,
  cleanupFn,
}: CheckoutUpsertParams): Promise<CheckoutFragment | undefined> => {
  const updateVars =
    checkoutId && lineItems && storeSlug
      ? {
          id: checkoutId,
          data: {
            storeSlug,
            lineItems,
            promoCode: '',
          },
        }
      : undefined;

  const createVars =
    lineItems && lineItems.length > 0 && storeSlug
      ? {
          affiliate: cookieStorage.getItem('affiliate') || '',
          lineItems,
          storeSlug,
          trafficSource: cookieStorage.getItem('traffic_source') || 'direct',
          currency,
        }
      : undefined;

  let checkoutRecordToUse: Maybe<CheckoutFragment>;
  if (updateVars) {
    try {
      const existingCheckoutRecord = await getCheckout(updateVars.id, updateVars.data.storeSlug);
      if (
        !isCheckoutFragment(existingCheckoutRecord) ||
        !['pending', 'created', undefined, null].includes(existingCheckoutRecord.state)
      ) {
        throw new Error('Invalid existing checkout record');
      }
      checkoutRecordToUse = await updateAndVerifyCheckout(updateVars);
    } catch (e) {
      logger.warn(`Error updating checkout, falling back to create: ${e}`);
      checkoutRecordToUse = undefined;
      cleanupFn();
    }
  }

  if (!checkoutRecordToUse && createVars) {
    try {
      checkoutRecordToUse = await createAndVerifyCheckout(createVars);
    } catch (e) {
      throw ensureIsCheckoutErrorObj(e, 'checkout');
    }
  }

  if (isCheckoutFragment(checkoutRecordToUse)) {
    return checkoutRecordToUse;
  } else {
    throw new Error('Error creating or updating checkout');
  }
};

/**
 * Builds line items for the pricing hook from validated cart products.
 *
 * @param {any[]} validatedCartProducts - The validated cart products.
 * @returns {LineItemData[]} - The line items for the pricing hook.
 */
const getLineItemsForPricingHook = (validatedCartProducts: any[]): LineItemData[] => {
  return validatedCartProducts.map((cartProduct) => {
    const { cartItem, product, variant, size } = cartProduct;
    const sku = buildSku(cartItem.itemGroupId, cartItem.variationId, cartItem.sizeId);
    return {
      imageUrl: variant?.images?.[0]?.small || product?.images?.[0]?.src || '',
      title: product?.title || '',
      productType: product?.primaryProduct?.[0]?.productType || '',
      size: size?.label || '',
      color: variant?.color || '',
      quantity: cartItem.quantity,
      subTotal: Number(size.price) * cartItem.quantity,
      currency: isCurrency(cartProduct.variant.currency) ? cartProduct.variant.currency : DEFAULT_CURRENCY,
      sku,
    };
  });
};

/**
 * Retrieves initial requyired parameters for the pricing hook to set pricing info before taxes/shipping etc..
 *
 * @param {LineItemData[]} summaryLineItems - The summary line items.
 * @param {any} cartMeta - The cart metadata.
 * @param {string} storeSlug - The store slug.
 * @returns {CheckoutPricingInformation | undefined} - The initial pricing information.
 */
const getInitParamsForPricingHook = (
  summaryLineItems: LineItemData[],
  cartMeta: any,
  storeSlug: string
): CheckoutPricingInformation | undefined => {
  if (summaryLineItems && cartMeta && storeSlug) {
    return {
      subtotal: flattenMoney(cartMeta.subTotal),
      currency: isCurrency(cartMeta.currency) ? cartMeta.currency : DEFAULT_CURRENCY,
      quantity: cartMeta.quantity,
    };
  }
  return undefined;
};

/**
 * Calculates costs for the pricing hook based on the checkout record and base pricing information (shipping/tax included).
 *
 * @param {any} checkoutRecord - The checkout record.
 * @param {CheckoutPricingInformation} basePricingInfo - The base pricing information.
 * @returns {CheckoutPricingInformation} - The calculated pricing information.
 */
const getCostsForPricingHook = (
  checkoutRecord: any,
  basePricingInfo: CheckoutPricingInformation
): CheckoutPricingInformation => {
  const costs = checkoutRecord?.costs;

  if (costs?.total?.value > 0) {
    return {
      ...basePricingInfo,
      shipping: costs.shipping?.value,
      tax: costs.tax?.value,
      discount: costs.discount?.value,
      total: costs.total?.value,
    };
  }

  // If costs are not valid, return only basePricingInfo with undefined cost fields
  return {
    ...basePricingInfo,
    shipping: undefined,
    tax: undefined,
    discount: undefined,
    total: undefined,
  };
};

/**
 * Transforms shipping quotes into format used in UI to display shipping options
 *
 * @param {DeliveryOptionsFragment['attributes']['quotes']} quotes - The shipping quotes.
 * @param {string} matchingQuoteName - The name of the matching quote.
 * @returns {ShippingQuote[]} - The transformed shipping quotes.
 */
const transformShippingQuotes = (
  quotes: DeliveryOptionsFragment['attributes']['quotes'],
  matchingQuoteName: string
): ShippingQuote[] => {
  return quotes.map((quote) => {
    if (!quote?.cost?.amount) {
      throw new Error('Quote cost amount is missing');
    }
    const early = quote?.deliveryEstimates?.early ? new Date(quote.deliveryEstimates.early) : undefined;
    const late = quote?.deliveryEstimates?.late ? new Date(quote.deliveryEstimates.late) : undefined;
    const description =
      early && late
        ? `Delivery between ${early.getMonth() + 1}/${early.getDate()} and ${late.getMonth() + 1}/${late.getDate()}`
        : 'Delivery timeframe unknown.';

    return {
      name: quote.name || 'standard',
      active: quote.name === matchingQuoteName,
      early: early || 'Unknown',
      late: late || 'Unknown',
      title: quote.name ? `${capitalizeFirstChar(quote.name)} delivery` : '',
      description,
      amount: normalizeMoney(quote.cost.amount),
      currency: quote.cost.currency || DEFAULT_CURRENCY,
    };
  });
};

/**
 * Retrieves shipping parameters used to get shipping rates for a particular address/checkout.
 *
 * @param {Object} params - The input parameters.
 * @param {CheckoutFragment} params.checkoutRecord - The checkout record.
 * @param {Currency} params.currency - The currency.
 * @param {string} params.storeSlug - The store slug.
 * @param {Object} params.customerInfo - The customer information.
 * @returns {Object | undefined} - The shipping parameters or undefined if invalid.
 */
const getShippingParams = ({
  checkoutRecord,
  currency,
  storeSlug,
  customerInfo,
}: {
  checkoutRecord: CheckoutFragment | undefined;
  currency: Currency | undefined;
  storeSlug: string | undefined;
  customerInfo: { address: { country: string; state?: string } } | undefined;
}) => {
  if (!checkoutRecord || !currency || !storeSlug) {
    return undefined;
  }

  return {
    checkoutRecord,
    currency,
    storeSlug,
    address: {
      // These defaults work because we only allow USA shipping for now - need to find a way to get geoIP data
      // from here (or sent down as an arg)
      countryCode: customerInfo?.address?.country || 'USA',
      stateCode: customerInfo?.address?.state ? `US-${customerInfo.address.state.toUpperCase()}` : undefined,
    },
  };
};

/**
 * Retrieves parameters for updating shipping rate changed by the customer.
 *
 * @param {Object} params - The input parameters.
 * @param {CheckoutFragment} [params.checkoutRecord] - The checkout record.
 * @param {string} [params.storeSlug] - The store slug.
 * @returns {Object | undefined} - The shipping update parameters or undefined if invalid.
 */
const getShippingUpdateParams = ({
  checkoutRecord,
  storeSlug,
}: {
  checkoutRecord?: CheckoutFragment;
  storeSlug?: string;
}) => {
  if (!checkoutRecord?.id || !storeSlug) {
    return undefined;
  }

  return {
    checkoutRecordId: checkoutRecord.id,
    storeSlug,
  };
};

/**
 * Fetches shipping rates based on the provided parameters.
 *
 * @param {GetShippingParams} shippingVars - The shipping parameters.
 * @returns {Promise<ShippingRate[]>} - The fetched shipping rates.
 */
export const fetchShippingRates = async (shippingVars: GetShippingParams) => {
  try {
    const maybeShippingResults = await getShippingRates(
      shippingVars.checkoutRecord,
      shippingVars.currency,
      shippingVars.address,
      shippingVars.storeSlug,
      false
    );
    if (!isDeliveryOptionsFragment(maybeShippingResults)) {
      throw maybeShippingResults; // most likely an error fragment
    }
    return maybeShippingResults;
  } catch (e) {
    logger.error(e);
    throw new CheckoutErrorObj('UNPROCESSABLE_SHIPPING_RATES', 'Error fetching shipping rates');
  }
};

/**
 * Handles address verification using Smarty Streets.
 * @param customerAddress
 * @returns {Promise<AddressVerificationFragment>}
 */
export const handleAddressVerification = async (customerAddress: any) => {
  const verifiedAddress = await addressVerification(customerAddress.data.customer.address);
  if (verifiedAddress?.addressValid) {
    return verifiedAddress;
  } else {
    throw new CheckoutErrorObj('SMARTY_STREETS_INVALID_ERROR', 'Smart streets returned address as invalid');
  }
};

/**
 * Checks if there are changes in line items between the old and new arrays - used for init to know if we need to update the checkout
 * @param old
 * @param maybeNew
 */
const hasLineItemsChanges = (old: LineItemFulfillmentDetailsInput[], maybeNew: LineItemFulfillmentDetailsInput[]) => {
  if (!maybeNew || maybeNew.length === 0) return false;
  if (old.length !== maybeNew.length) return true;
  return old.some((oldItem) => {
    const newItem = maybeNew.find((item) => item.sku === oldItem.sku);
    return !newItem || oldItem.quantity !== newItem.quantity;
  });
};

export {
  deliveryOptionToCheckoutDeliveryOption,
  checkoutDeliveryOptionToDeliveryOption,
  checkoutErrorToDisplay,
  buildCheckoutConfirmationRoute,
  isDeliveryOptionsFragment,
  isCheckoutErrorObj,
  getUnknownErrorByContext,
  ensureIsCheckoutErrorObj,
  suggestedAddressFragmentToPartialCheckoutAddress,
  parseStripeEventNameToFirstLast,
  buildLineItemsFulfillmentDetailsFromCart,
  mapDeliveryOptionsToShippingRates,
  getShippingRates,
  handleVerificationSelectedAddress,
  requiresUserConfirmation,
  createConfirmationToken,
  confirmPayment,
  getInitParamsForPricingHook,
  getLineItemsForPricingHook,
  getCostsForPricingHook,
  transformShippingQuotes,
  getShippingUpdateParams,
  getShippingParams,
  hasLineItemsChanges,
};
